import { generatePath, useNavigate } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { useToaster } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { getCompanyTravellersQueryKey } from 'api/company-traveller/hooks';
import { waitForElasticRefresh } from 'api/hooks';
import { routePaths } from 'ui/authenticated/navigation';
import { isInTemplateRoute } from 'ui/router';

import {
  createJourneyPollingConfig,
  CreatePollingConfigType,
  deleteJourneyPollingConfig,
  loadJourneyPollingConfig,
  loadJourneyPollingConfigs,
  loadJourneyPollingLog,
  loadJourneyPollingLogs,
  loadTemplateJourneyPollingConfig,
  loadTemplateJourneyPollingConfigs,
  PollingConfig,
  PollingConfigList,
  pollingTestRequest,
  PollingTestRequestData,
  pollJourneyPollingSchedule,
  updateJourneyPollingConfig,
} from './requests';

const getJourneyPollingKey = (journeyShortId: string) => [
  'journey',
  journeyShortId,
  'polling-configs',
];

const getJourneyPollingConfigKey = (pollingConfigId: string) => [
  'journey',
  pollingConfigId,
  'polling-config',
];

const useInvalidateJourneyPollingConfigs = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyPollingConfigs = (shortId: string) =>
    queryClient.invalidateQueries(getJourneyPollingKey(shortId));

  const invalidateJourneyPollingConfig = (pollingConfigId: string) =>
    queryClient.invalidateQueries(getJourneyPollingConfigKey(pollingConfigId));

  return { invalidateJourneyPollingConfigs, invalidateJourneyPollingConfig };
};

export const useCreateJourneyPollingConfig = () => {
  const { invalidateJourneyPollingConfigs } = useInvalidateJourneyPollingConfigs();
  return useMutation({
    mutationFn: (data: CreatePollingConfigType) => createJourneyPollingConfig(data),
    onSuccess: (data) => {
      invalidateJourneyPollingConfigs(data.journeyShortId);
    },
  });
};

export const useUpdateJourneyPollingConfig = (pollingConfig: PollingConfigList) => {
  const { invalidateJourneyPollingConfigs, invalidateJourneyPollingConfig } =
    useInvalidateJourneyPollingConfigs();
  return useMutation({
    mutationFn: (pollingConfigData: Partial<PollingConfig>) =>
      updateJourneyPollingConfig(pollingConfig.shortId, pollingConfigData),
    onSuccess: () => {
      invalidateJourneyPollingConfigs(pollingConfig.journeyShortId);
      invalidateJourneyPollingConfig(pollingConfig.shortId);
    },
  });
};

export const useJourneyPollingConfigsList = (journeyShortId: string) => {
  const loadFn = isInTemplateRoute()
    ? loadTemplateJourneyPollingConfigs
    : loadJourneyPollingConfigs;
  return useQuery({
    queryKey: getJourneyPollingKey(journeyShortId),
    queryFn: () => loadFn(journeyShortId),
  });
};

export const useJourneyPollingConfig = (pollingConfigShortId: string) => {
  const loadFn = isInTemplateRoute() ? loadTemplateJourneyPollingConfig : loadJourneyPollingConfig;
  return useQuery({
    queryKey: getJourneyPollingConfigKey(pollingConfigShortId),
    queryFn: () => loadFn(pollingConfigShortId),
  });
};

export const useDeleteJourneyPollingConfig = (journeyShortId: string) => {
  const { invalidateJourneyPollingConfigs } = useInvalidateJourneyPollingConfigs();
  return useMutation({
    mutationFn: (pollingConfigId: string) => deleteJourneyPollingConfig(pollingConfigId),
    onSuccess: () => {
      invalidateJourneyPollingConfigs(journeyShortId);
    },
  });
};

export const usePollNowJourneyPollingConfig = (
  pollingConfigId: PollingConfig['shortId'],
  journeyShortId: string,
  workspaceId: string
) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => pollJourneyPollingSchedule(pollingConfigId),
    onSuccess: (data: { numTravellerJourneysCreated: number }) => {
      showSuccess(t('fetchNowSuccess', { num_travellers: data.numTravellerJourneysCreated ?? 0 }), {
        customButtons: [
          {
            icon: <IconOpenNewTab />,
            text: t('viewTravellers'),
            onClick: () => {
              const path = generatePath(routePaths.journeyDetails, {
                workspaceId,
                journeyId: journeyShortId,
                '*': null,
              });
              navigate(path);
            },
          },
        ],
      });
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers', journeyShortId]);
        queryClient.invalidateQueries(getCompanyTravellersQueryKey('full'));
        queryClient.invalidateQueries(getJourneyPollingConfigKey(pollingConfigId));
      });
    },
    onError: () => {
      showError(t('fetchNowError'));
    },
    retry: false,
  });
};

export const usePollingTestRequest = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PollingTestRequestData) => pollingTestRequest(data),
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries(['integrations', journeyShortId, 'polling-endpoints']);
        showSuccess(t('pollingRequestSuccess'));
      } else {
        showError(data.errorMessage);
      }
    },
    onError: () => {
      showError(t('pollingRequestError'));
    },
  });
};

export const useJourneyPollingLogs = (pollingConfigShortId: string) => {
  return useQuery({
    queryKey: ['journeys', pollingConfigShortId, 'polling', 'logs'],
    queryFn: () => loadJourneyPollingLogs(pollingConfigShortId),
  });
};

export const useJourneyPollingLog = (pollingConfigShortId: string, pollingLogShortId: string) => {
  return useQuery({
    queryKey: ['journeys', pollingConfigShortId, 'polling', 'logs', pollingLogShortId],
    queryFn: () => loadJourneyPollingLog(pollingConfigShortId, pollingLogShortId),
  });
};

const locale = {
  en: {
    fetchNowSuccess:
      'Polling Successful! {num_travellers} new Travellers have been created in your journey.',
    viewTravellers: 'View travellers',
    fetchNowError: 'Failed to fetch polling configuration.',
    pollingRequestSuccess: 'Endpoint has a valid payload.',
    pollingRequestError: 'Failed to test polling request.',
  },
};
